import { useState, useEffect, FC, MouseEventHandler, ChangeEvent, FocusEvent } from 'react';
import classnames from 'classnames';

import HeaderRangeCalculator, { IHeaderRangeCalculatorProps } from './HeaderRangeCalculator';
import ContentRangeCalculator, { IContentRangeCalculatorProps } from './ContentRangeCalculator';
import FooterRangeCalculator, { IFooterRangeCalculatorProps } from './FooterRangeCalculator';

export interface IClassesRangeCalculatorProps {
  wrapper?: string;
  header?: string;
  headerTitle?: string;
  headerInputBlock?: string;
  headerInputBlockInput?: string;
  headerInputBlockSpan?: string;
  content?: string;
  contentButtonMinus?: string;
  contentButtonPlus?: string;
  contentSlider?: string;
  footer?: string;
  footerMinValue?: string;
  footerMaxValue?: string;
}

export interface IRangeCalculatorComponentProps extends IRangeCalculatorExtendsProps {
  HeaderRangeCalculatorComponent?: FC<IHeaderRangeCalculatorProps>;
  ContentRangeCalculatorComponent?: FC<IContentRangeCalculatorProps>;
  FooterRangeCalculatorComponent?: FC<IFooterRangeCalculatorProps>;
}

export interface IRangeCalculatorExtendsProps
  extends Pick<IContentRangeCalculatorProps, 'buttonMinus' | 'buttonPlus'> {}

export interface IRangeCalculatorProps extends IRangeCalculatorExtendsProps {
  steps: number[];
  value: number;
  onChange: (value: number) => void;
  prefixPosition?: 'first' | 'last';
  prefix?: string;
  label?: string;
  parseValue?: (value: string | number) => number;
  formatValue?: (value: string | number) => string;
  classes?: IClassesRangeCalculatorProps;
  className?: string;
}

const RangeCalculator: FC<IRangeCalculatorProps & IRangeCalculatorComponentProps> = ({
  steps,
  value,
  onChange,
  prefix,
  prefixPosition,
  label,
  parseValue,
  formatValue,
  buttonMinus,
  buttonPlus,
  classes,
  className,
  HeaderRangeCalculatorComponent,
  ContentRangeCalculatorComponent,
  FooterRangeCalculatorComponent,
}) => {
  const minIndex = 0;
  const maxIndex = steps.length - 1;

  const minStep = steps[minIndex];
  const maxStep = steps[maxIndex];

  const [currentIndex, setCurrentIndex] = useState<number>(steps.findIndex((v) => v === value));
  const [inputValue, setInputValue] = useState<number>(value);

  useEffect(() => {
    setInputValue(value);

    // set position of handler if value changed:
    const steps_ = [...steps];
    const closestNumber = steps_.sort((x, y) => Math.abs(value - x) - Math.abs(value - y))[0];
    setCurrentIndex(steps.findIndex((v) => v === closestNumber));
  }, [value]);

  useEffect(() => {
    if (steps.length) {
      setInputValue(steps[currentIndex]);
      onChange(steps[currentIndex]);
    }
  }, [currentIndex]);

  const _parseValue = (value: string | number): number => (parseValue ? parseValue(value) : +value);
  const _formatValue = (value: string | number): string =>
    formatValue ? formatValue(value) : value.toString();

  const onChangeInputHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(_parseValue(e.target.value));
  };

  const onBlurInputHandler = (e: FocusEvent<HTMLInputElement>) => {
    const value = _parseValue(e.target.value);
    const steps_ = [...steps];
    const closestNumber = steps_.sort((x, y) => Math.abs(value - x) - Math.abs(value - y))[0];

    setCurrentIndex(steps.findIndex((v) => v === closestNumber));
    setInputValue(closestNumber);
  };

  const onDecreaseHandler: MouseEventHandler<HTMLButtonElement> = () => {
    setCurrentIndex(currentIndex - 1);
  };

  const onIncreaseHandler: MouseEventHandler<HTMLButtonElement> = () => {
    setCurrentIndex(currentIndex + 1);
  };

  const headerRangeCalculatorProps = {
    label,
    inputValue: _formatValue(+inputValue),
    maxLength: maxStep && _formatValue(maxStep).length,
    onChangeInputHandler,
    onBlurInputHandler,
    prefix,
    prefixPosition,
    classHeaderTitle: classes?.headerTitle,
    classHeaderInputBlock: classes?.headerInputBlock,
    classHeaderInputBlockInput: classes?.headerInputBlockInput,
    classHeaderInputBlockSpan: classes?.headerInputBlockSpan,
  };

  const contentRangeCalculatorProps = {
    step: 1,
    minIndex,
    maxIndex,
    currentIndex,
    setCurrentIndex,
    onDecreaseHandler,
    onIncreaseHandler,
    buttonMinus,
    buttonPlus,
    classContentButtonMinus: classes?.contentButtonMinus,
    classContentSlider: classes?.contentSlider,
    classContentButtonPlus: classes?.contentButtonPlus,
  };

  const footerRangeCalculatorProps = {
    minStep: minStep ? _formatValue(minStep) : '',
    maxStep: maxStep ? _formatValue(maxStep) : '',
    prefix,
    prefixPosition,
    classFooterMinValue: classes?.footerMinValue,
    classFooterMaxValue: classes?.footerMaxValue,
  };

  return (
    <div className={classnames('range', classes?.wrapper, className)}>
      <div className={classnames('range__header', classes?.header)}>
        {HeaderRangeCalculatorComponent ? (
          <HeaderRangeCalculatorComponent {...headerRangeCalculatorProps} />
        ) : (
          <HeaderRangeCalculator {...headerRangeCalculatorProps} />
        )}
      </div>
      <div className={classnames('range__content', classes?.content)}>
        {ContentRangeCalculatorComponent ? (
          <ContentRangeCalculatorComponent {...contentRangeCalculatorProps} />
        ) : (
          <ContentRangeCalculator {...contentRangeCalculatorProps} />
        )}
      </div>
      <div className={classnames('range__footer', classes?.footer)}>
        {FooterRangeCalculatorComponent ? (
          <FooterRangeCalculatorComponent {...footerRangeCalculatorProps} />
        ) : (
          <FooterRangeCalculator {...footerRangeCalculatorProps} />
        )}
      </div>
    </div>
  );
};

export default RangeCalculator;
